import React, { useContext, useEffect, useMemo, useState } from "react";
import { Checkbox } from "../../../../components/Checkbox/Checkbox";
import { InputText } from "../../../../components/inputText/InputText";
import { DropDown } from "../../../../components/dropDown/DropDown";
import { LabelBox } from "../../../../components/labelBox/LabelBox";
import { postClient, uploadAvatar } from "../../../../api/api";
import { getDatesList, getMonthList, getYearsList } from "../../../../utils/yearsGen";
import { AvatarBlock } from "./AvatarBlock";
import './AddClient.css';
import { monthNames } from "../../../../utils/dateFormats";
import { IClientData, IClientFormData } from "../../../../types/dashboard";
import { AppContext } from "../../../../appContext";
import { ConfirmationPopup } from "../../../../components/confirmationPopup/ConfirmationPopup";

/*interface IClientFormData {
    firstName: string;
    lastName: string;
    gender: "" | "male" | "female";
    email: string;
    dateOfBirth: string;
    notes: string;
    scheduleDay: number;
}*/

type IClientFormErrors = Partial<Record<keyof IClientFormData, string>>;

const validateClientForm = (data: IClientFormData): {errors: IClientFormErrors, isValid: boolean} => {
    const errors: IClientFormErrors = {};

    if (!data.firstName){
        errors.firstName = 'First name is required';
    }

    if (!data.lastName){
        errors.lastName = 'Last name is required';
    }

    const isValid = Object.values(errors).filter(it => it).length == 0;
    return {
        errors,
        isValid
    }
}

const DropDownCalendar = ({onDate, date}:{onDate: (date: Date)=>void, date?: string})=>{
    const [yearIndex, setYearIndex] = useState<number>(-1);
    const [monthIndex, setMonthIndex] = useState<number>(-1);
    const [dayIndex, setDayIndex] = useState<number>(-1);

    const yearsList = useMemo(()=>getYearsList(), []);
    const monthList = useMemo(()=>getMonthList().map((it)=>monthNames[it]), []);
    const datesList = getDatesList(yearsList[yearIndex] || 0, monthIndex || 0);

    useEffect(()=>{
        if (!date){
            return;
        }
        const d = new Date(date);
        setYearIndex(yearsList.findIndex(it=>it==d.getUTCFullYear()));
        setMonthIndex(d.getUTCMonth());
        setDayIndex(d.getUTCDate());
    }, [date]);

    useEffect(()=>{
        if (yearIndex == -1 || monthIndex == -1 || dayIndex == -1){
            return;
        }
        const date = new Date(0);
        date.setFullYear(yearsList[yearIndex]);
        date.setMonth(monthIndex);
        date.setDate(datesList[Math.min(dayIndex, datesList.length - 1)]);
        onDate(date);
    }, [yearIndex, monthIndex, dayIndex]);

    return (
        <>
        <LabelBox className="dropDownCalendar_labelBox" label="Date of birth - Year">
            <DropDown className="dropDownCalendar_dropDown" values={yearsList} selectedIndex={yearIndex} maxHeight={200} onSelect={(index)=>{setYearIndex(index)}}></DropDown>
        </LabelBox>
        <LabelBox className="dropDownCalendar_labelBox" label="Month">
            <DropDown className="dropDownCalendar_dropDown" values={monthList} selectedIndex={monthIndex} maxHeight={200} onSelect={(index)=>{setMonthIndex(index)}}></DropDown>  
        </LabelBox>
        <LabelBox className="dropDownCalendar_labelBox" label="Day">
            <DropDown className="dropDownCalendar_dropDown" values={datesList} selectedIndex={Math.min(dayIndex, datesList.length - 1)} maxHeight={200} onSelect={(index)=>{setDayIndex(index)}}></DropDown>  
        </LabelBox>
        </>
    )
}

interface IAddClientProps{
    onClose: ()=>void;
    onSave: (data: any)=>void;
    onSaveDraft: ()=>void;
    onDelete?: (id: string)=>void;
    client?: IClientData;
}

export const AddClient = ({onClose, onSave, onDelete, client}: IAddClientProps)=>{
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [dateOfBirth, setDateOfBirth] = useState('');
    const [email, setEmail] = useState('');
    const [gender, setGender] = useState<'male' | 'female' | '' | 'other'>('');
    const [notes, setNotes] = useState('');
    const [scheduleDay, setScheduleDay] = useState(-1);
    const [avatar, setAvatar] = useState<HTMLCanvasElement>(null);
    const [errors, setErrors] = useState<IClientFormErrors>({});
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    const {cheat, translate} = useContext(AppContext);

    const handleDeleteClick = () => {
        setShowDeleteConfirm(true);
    }

    const handleDeleteConfirm = () => {
        setShowDeleteConfirm(false);
        onDelete(client.id);
    }

    const handleDeleteCancel = () => {
        setShowDeleteConfirm(false);
    }

    const handleAdd = ()=>{
        const newClientData = {
            firstName, 
            lastName,
            gender: gender == '' ? 'other' : gender,
            email,
            dateOfBirth,
            notes,
            scheduleDay,
            id: client?.id
        };
        const {errors, isValid} = validateClientForm(newClientData);
        setErrors(errors);

        if (!isValid){
            return;
        }

        if (avatar){
            avatar.toBlob((blob)=>{
                uploadAvatar(blob, ()=>{}).then(res=>{
                    console.log('avatar result: ', res);
                    //res.url
                    onSave({...newClientData, customAvatarUrl: res.url});
                })
            }, "image/png");
        } else {
            onSave(newClientData);
        }
    }

    useEffect(()=>{
        if (!client){
            return;
        }
        setFirstName(client.firstName || client.name.split(' ')[0]);
        setLastName(client.name.split(' ')[1]);
        setGender(client.gender || '');
        setEmail(client.email || '');
        setDateOfBirth(client.dateOfBirth || '');
        setNotes(client.notes || '');
        setScheduleDay(client.scheduleDay || -1);
    }, [client]);

    return <div className="addClient">
        <div className="addClient_form">
            <div className="addClient_header">
                <div className="addClient_headerTitle">
                    {client ? `Edit client ${client.name}` : 'Add a new client'}
                </div>
                <div className="button addClient_icon addClient_close_icon" onClick={onClose}></div>
            </div>
            <div className="addClient_scrollWrap">
            <div className="addClient_inputs">
            <AvatarBlock onImageUpdate={(avatar) => setAvatar(avatar)} initialAvatarUrl={client?.customAvatarUrl}></AvatarBlock>
            <InputText 
                className="addClient_firstName" 
                label="First name" 
                value={firstName} 
                onChange={(e)=>{setFirstName(e)}}
                error={errors.firstName}
            ></InputText>
            <InputText 
                className="addClient_lastName" 
                label="Last name" 
                value={lastName} 
                onChange={(e)=>{setLastName(e)}}
                error={errors.lastName}
            ></InputText>
            {/*<InputText className="addClient_dateOfBirth" label="Date of birth" value={dateOfBirth} onChange={(e)=>{setDateOfBirth(e)}}></InputText>*/}
            <div className="addClient_dateOfBirth">
                <DropDownCalendar
                    date={client?.dateOfBirth}
                    onDate={(date)=>{
                        setDateOfBirth(Number.isNaN(date.valueOf()) ? '' : date.toISOString())
                    }}></DropDownCalendar>
            </div>
            <InputText className="addClient_email" label="Email" value={email} onChange={(e)=>{setEmail(e)}}></InputText>

            <div className="addClient_schedule">
                <div className="addClient_schedule_label"></div>
                <div className="addClient_schedule_content">
                    <Checkbox label="Female" checked={gender=='female'} onCheck={()=>{setGender((last)=> last == 'female' ? '': 'female')}}></Checkbox>
                    <Checkbox label="Male" checked={gender=='male'} onCheck={()=>{setGender((last)=> last == 'male' ? '': 'male')}}></Checkbox>
                    <Checkbox label="Other" checked={gender=='other'} onCheck={()=>{setGender((last)=> last == 'other' ? '': 'other')}}></Checkbox>
                </div>
            </div>
            {cheat.debug && 
                <InputText className="addClient_notes" 
                    label="Notes" 
                    value={notes} 
                    onChange={(e)=>{setNotes(e)}}
                ></InputText>
            }
            {cheat.debug && 
                <div className="addClient_schedule">
                    <div className="addClient_schedule_label">Schedule</div>
                    <div className="addClient_schedule_content">
                        {days.map((it, i)=>(
                            <Checkbox 
                                label={it} checked={scheduleDay == i}
                                onCheck={()=>{
                                    setScheduleDay((last)=>last == i ? -1 : i)
                                }}>
                            </Checkbox>
                        ))}
                    </div>
                </div>
            }
            </div>
            </div>
            <div className="addClient_buttons">
                {client && <div className="button addClient_delete" onClick={handleDeleteClick}>
                    {translate('common_delete')}
                </div>}
                <div className="addClient_buttons_right">
                    <div className="button saveButton addClient_save" onClick={handleAdd}>
                        {client ? translate('common_save') : translate('common_create')}
                    </div>
                </div>
            </div>
        </div>
        {showDeleteConfirm && (
            <ConfirmationPopup
                message={translate('client_confirm_delete')}
                confirmText={translate('common_delete')}
                cancelText={translate('common_cancel')}
                onConfirm={handleDeleteConfirm}
                onCancel={handleDeleteCancel}
                confirmButtonStyle="danger"
            />
        )}
    </div>
}
